/**
 * OrderCreate — admin "New Order" (telesales desk) page at `/orders/new`.
 *
 * Staff build an order on behalf of an existing approved customer at a chosen
 * company location, then settle it as COD or mark-paid (on account). It is a
 * single page (not a wizard) with four stacked sections:
 *
 *   1. Customer — company search → pick a company → pick a location.
 *   2. Lines    — product search → pick a product → pick a variant → append a
 *                 line. Qty + an optional ex-VAT net price override per line.
 *   3. Preview  — once a location + ≥1 complete line + a selectable settlement
 *                 exist, POST the draft to /v1/admin/orders/preview (debounced)
 *                 and render the SERVER totals. No client pricing math.
 *   4. Settle + Create — radio filtered to the location's allowed methods, then
 *                 POST /v1/admin/orders → navigate to /orders/:order_id.
 *
 * The server re-prices everything; this page never computes a price. The only
 * client arithmetic is summing already-server-resolved per-line values for the
 * line-total display column (`unit*qty + vat`); the authoritative subtotal/
 * vat/total come straight from the preview response.
 *
 * The preview endpoint runs the SAME settlement→allowed_payment_methods gate as
 * create, so we only ever call it once a valid location AND a selectable
 * settlement method exist — otherwise it would 422 on the gate.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  Numeric,
  Radio,
  Select,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import { formatGBP } from "../lib/format.js";
import {
  OrderLineEditor,
  type OrderLineRow,
  type PricedLine,
} from "../components/OrderLineEditor.js";
import type {
  AdminCreateOrderBody,
  CompaniesListResponse,
  CompanyDetailDTO,
  CompanyDTO,
  CompanyLocationDTO,
  ProductDTO,
  ProductDetailDTO,
  VariantDTO,
} from "@wmart/shared-types";

// ---------------------------------------------------------------------------
// Preview wire shapes — mirror create-order.service.ts `PreviewResult` /
// `PreviewLine`. (Not exported from shared-types; the service returns this
// shape verbatim from POST /v1/admin/orders/preview.)
// ---------------------------------------------------------------------------

interface PreviewLineResponse {
  variant_id: string;
  quantity: number;
  unit_price_minor: number;
  vat_amount_minor: number;
  overridden: boolean;
}

interface PreviewResponse {
  lines: PreviewLineResponse[];
  subtotal_minor: number;
  vat_minor: number;
  total_minor: number;
  currency: "GBP";
}

type SettlementMethod = AdminCreateOrderBody["settlement"]["method"]; // "cod" | "paid"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** A line is "complete" (preview-able) when it has a variant + a positive int qty. */
function isCompleteLine(row: OrderLineRow): boolean {
  if (!row.variant_id) return false;
  const q = Number(row.quantity);
  return Number.isInteger(q) && q > 0;
}

/** Parse the optional override; "" → undefined, else a non-negative int (or undefined if junk). */
function parseOverride(raw: string): number | undefined {
  const t = raw.trim();
  if (t === "") return undefined;
  const n = Number(t);
  if (!Number.isInteger(n) || n < 0) return undefined;
  return n;
}

/**
 * Which settlement methods can be offered for this (company, location)?
 *   - COD  iff `company.cod_enabled === true` — a COMPANY-level flag. The
 *     server gates COD on this flag (422 `order.cod_not_enabled` otherwise);
 *     `allowed_payment_methods` never carries "cod", so it plays no part here.
 *   - paid iff `location.allowed_payment_methods.includes("bank_transfer")` —
 *     mark-paid settles as a bank transfer (the server enforces this mapping).
 */
function availableSettlements(
  company: CompanyDetailDTO | null,
  location: CompanyLocationDTO | null,
): SettlementMethod[] {
  const out: SettlementMethod[] = [];
  if (company?.cod_enabled === true) out.push("cod");
  if (location?.allowed_payment_methods.includes("bank_transfer")) {
    out.push("paid");
  }
  return out;
}

const SETTLEMENT_LABEL: Record<SettlementMethod, string> = {
  cod: "Cash on delivery (COD)",
  paid: "Mark paid / on account",
};

/** Build the create/preview request body from the current draft. */
function buildBody(
  companyLocationId: string,
  rows: OrderLineRow[],
  method: SettlementMethod,
): AdminCreateOrderBody {
  return {
    company_location_id: companyLocationId,
    line_items: rows.filter(isCompleteLine).map((r) => {
      const override = parseOverride(r.unit_price_override_minor);
      return {
        variant_id: r.variant_id,
        quantity: Number(r.quantity),
        ...(override !== undefined
          ? { unit_price_override_minor: override }
          : {}),
      };
    }),
    settlement: { method },
  };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function OrderCreate() {
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();

  // --- Customer section state ---
  const [companyQ, setCompanyQ] = useState("");
  const [companyResults, setCompanyResults] = useState<CompanyDTO[]>([]);
  const [companySearching, setCompanySearching] = useState(false);
  const [company, setCompany] = useState<CompanyDetailDTO | null>(null);
  const [companyLocationId, setCompanyLocationId] = useState("");

  // --- Lines section state ---
  const [rows, setRows] = useState<OrderLineRow[]>([]);
  const [productQ, setProductQ] = useState("");
  const [productResults, setProductResults] = useState<ProductDTO[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const [activeProduct, setActiveProduct] = useState<ProductDetailDTO | null>(
    null,
  );

  // --- Settlement ---
  const [method, setMethod] = useState<SettlementMethod | "">("");

  // --- Preview ---
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  // --- Create ---
  const [creating, setCreating] = useState(false);

  const selectedLocation = useMemo<CompanyLocationDTO | null>(() => {
    if (!company) return null;
    return (
      company.locations.find((l) => l.id === companyLocationId) ?? null
    );
  }, [company, companyLocationId]);

  const settlements = useMemo(
    () => availableSettlements(company, selectedLocation),
    [company, selectedLocation],
  );

  const completeRows = useMemo(() => rows.filter(isCompleteLine), [rows]);

  // -------------------------------------------------------------------------
  // Company search (debounced).
  // -------------------------------------------------------------------------
  useEffect(() => {
    const q = companyQ.trim();
    if (q === "") {
      setCompanyResults([]);
      return;
    }
    let cancelled = false;
    setCompanySearching(true);
    const t = setTimeout(() => {
      void (async () => {
        try {
          const data = await api.get<CompaniesListResponse>(
            `/v1/admin/companies?q=${encodeURIComponent(q)}`,
          );
          if (!cancelled) setCompanyResults(data.items);
        } catch (err) {
          if (!cancelled) {
            if (err instanceof ApiError && err.problem)
              notifyProblem(err.problem);
            else notify("error", "Failed to search companies");
          }
        } finally {
          if (!cancelled) setCompanySearching(false);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [companyQ, notify, notifyProblem]);

  async function selectCompany(c: CompanyDTO) {
    setCompanyQ("");
    setCompanyResults([]);
    try {
      const detail = await api.get<CompanyDetailDTO>(
        `/v1/admin/companies/${c.id}`,
      );
      setCompany(detail);
      // Default to the sole location if there is exactly one live one.
      const live = detail.locations.filter((l) => l.deleted_at === null);
      setCompanyLocationId(live.length === 1 ? live[0]!.id : "");
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load company");
    }
  }

  // When the location changes, reset settlement to the first available method.
  useEffect(() => {
    if (settlements.length === 0) {
      setMethod("");
      return;
    }
    setMethod((prev) =>
      prev && settlements.includes(prev) ? prev : settlements[0]!,
    );
  }, [settlements]);

  // -------------------------------------------------------------------------
  // Product search (debounced).
  // -------------------------------------------------------------------------
  useEffect(() => {
    const q = productQ.trim();
    if (q === "") {
      setProductResults([]);
      return;
    }
    let cancelled = false;
    setProductSearching(true);
    const t = setTimeout(() => {
      void (async () => {
        try {
          const data = await api.get<{ items: ProductDTO[] }>(
            `/v1/admin/products?q=${encodeURIComponent(q)}`,
          );
          if (!cancelled) setProductResults(data.items);
        } catch (err) {
          if (!cancelled) {
            if (err instanceof ApiError && err.problem)
              notifyProblem(err.problem);
            else notify("error", "Failed to search products");
          }
        } finally {
          if (!cancelled) setProductSearching(false);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [productQ, notify, notifyProblem]);

  async function pickProduct(p: ProductDTO) {
    setProductQ("");
    setProductResults([]);
    try {
      const detail = await api.get<ProductDetailDTO>(
        `/v1/admin/products/${p.id}`,
      );
      setActiveProduct(detail);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load product");
    }
  }

  function addLine(product: ProductDetailDTO, variant: VariantDTO) {
    setRows((prev) => [
      ...prev,
      {
        variant_id: variant.id,
        quantity: "1",
        unit_price_override_minor: "",
        product_title: product.title,
        variant_title: variant.title,
        sku: variant.sku,
      },
    ]);
    setActiveProduct(null);
  }

  // -------------------------------------------------------------------------
  // Preview (debounced). Fires only when a location + ≥1 complete line + a
  // selectable settlement exist — preview runs the same settlement gate as
  // create, so an unselectable method would 422.
  // -------------------------------------------------------------------------
  const canPreview =
    companyLocationId !== "" && completeRows.length > 0 && method !== "";

  // Serialise the inputs the preview depends on so the effect only re-runs on a
  // meaningful change (avoids refiring on display-only edits).
  const previewKey = useMemo(() => {
    if (!canPreview) return "";
    return JSON.stringify(buildBody(companyLocationId, rows, method as SettlementMethod));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPreview, companyLocationId, rows, method]);

  useEffect(() => {
    if (previewKey === "") {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    // `cancelled` mirrors the company/product search effects: it guards against
    // an out-of-order resolve. `clearTimeout` only cancels a not-yet-fired
    // debounce timer — an already-in-flight POST cannot be aborted, so a slow
    // older preview could otherwise resolve after a newer one and overwrite the
    // displayed totals. We skip every setState once the effect is cleaned up
    // (key changed / unmount).
    let cancelled = false;
    const body = buildBody(companyLocationId, rows, method as SettlementMethod);
    const t = setTimeout(() => {
      setPreviewing(true);
      void (async () => {
        try {
          const res = await api.post<PreviewResponse>(
            "/v1/admin/orders/preview",
            body,
          );
          if (cancelled) return;
          setPreview(res);
          setPreviewError(null);
        } catch (err) {
          if (cancelled) return;
          setPreview(null);
          if (err instanceof ApiError) {
            setPreviewError(
              err.problem?.detail ?? err.problem?.title ?? err.message,
            );
          } else {
            setPreviewError("Failed to price the order");
          }
        } finally {
          if (!cancelled) setPreviewing(false);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  // Map the preview's per-line figures back onto the editor rows by index.
  // The preview returns one line per complete input line in order, so we walk
  // the rows and consume preview lines as we hit complete ones.
  const pricedByRow = useMemo<Array<PricedLine | null>>(() => {
    if (!preview) return rows.map(() => null);
    let pi = 0;
    return rows.map((row) => {
      if (!isCompleteLine(row)) return null;
      const pl = preview.lines[pi++];
      if (!pl) return null;
      return {
        unit_price_minor: pl.unit_price_minor,
        vat_amount_minor: pl.vat_amount_minor,
        line_total_minor:
          pl.unit_price_minor * pl.quantity + pl.vat_amount_minor,
        overridden: pl.overridden,
      };
    });
  }, [preview, rows]);

  // -------------------------------------------------------------------------
  // Create.
  // -------------------------------------------------------------------------
  const nonApprovedCompany =
    company !== null && company.approval_status !== "approved";

  // Disable Create for a non-approved company so the button matches the banner
  // (the server 422s on a non-approved company; don't let the UX contradict its
  // own warning by appearing to allow the POST).
  const canCreate =
    companyLocationId !== "" &&
    completeRows.length > 0 &&
    method !== "" &&
    !nonApprovedCompany &&
    !creating;

  // Guard against double-submit racing the disabled-attr.
  const submittingRef = useRef(false);

  async function create() {
    if (!canCreate || submittingRef.current) return;
    submittingRef.current = true;
    setCreating(true);
    const body = buildBody(companyLocationId, rows, method as SettlementMethod);
    try {
      const res = await api.post<{ order_id: string }>(
        "/v1/admin/orders",
        body,
      );
      notify("success", "Order created");
      navigate(`/orders/${res.order_id}`);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else if (err instanceof ApiError) notify("error", err.message);
      else notify("error", "Failed to create order");
    } finally {
      submittingRef.current = false;
      setCreating(false);
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="flex flex-col gap-8 max-w-4xl">
      <h1 className="font-serif text-display-sm text-ink-900">New order</h1>

      {/* ---- 1. Customer ---- */}
      <section className="flex flex-col gap-3">
        <h2 className="text-body font-medium text-ink-900">Customer</h2>

        {company === null ? (
          <>
            <div className="w-96">
              <Input
                type="search"
                label="Search companies"
                placeholder="Search by name or slug…"
                value={companyQ}
                onChange={(e) => setCompanyQ(e.target.value)}
              />
            </div>
            {companyResults.length > 0 ? (
              <ul className="flex flex-col gap-1 rounded-sm border border-ink-200 bg-white p-1 max-w-md">
                {companyResults.map((c) => {
                  const approved = c.approval_status === "approved";
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        disabled={!approved}
                        onClick={() => void selectCompany(c)}
                        className="flex w-full items-center justify-between gap-3 rounded-sm px-3 py-2 text-left text-body-sm hover:bg-paper-warm disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="text-ink-900">{c.name}</span>
                        <span className="text-caption text-ink-500">
                          {approved ? c.default_tier : c.approval_status}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : companySearching ? (
              <p className="text-body-sm text-ink-500">Searching…</p>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4 rounded-sm border border-ink-200 bg-white px-4 py-3">
              <div className="flex flex-col">
                <span className="text-body font-medium text-ink-900">
                  {company.name}
                </span>
                <span className="text-caption text-ink-500">
                  {company.approval_status} · {company.default_tier}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCompany(null);
                  setCompanyLocationId("");
                }}
              >
                Change
              </Button>
            </div>

            {nonApprovedCompany ? (
              <p className="text-body-sm text-danger" role="alert">
                This company is not approved — the order cannot be created until
                it is approved.
              </p>
            ) : null}

            <div className="w-96">
              <Select
                label="Location"
                value={companyLocationId}
                onChange={(e) => setCompanyLocationId(e.target.value)}
              >
                <option value="">Select a location…</option>
                {company.locations
                  .filter((l) => l.deleted_at === null)
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code} · {l.discount_percent}% off ·{" "}
                      {l.allowed_payment_methods.join(", ") || "no methods"}
                    </option>
                  ))}
              </Select>
            </div>
          </div>
        )}
      </section>

      {/* ---- 2. Lines ---- */}
      <section className="flex flex-col gap-3">
        <h2 className="text-body font-medium text-ink-900">Lines</h2>

        <div className="w-96">
          <Input
            type="search"
            label="Add a product"
            placeholder="Search products by title or SKU…"
            value={productQ}
            onChange={(e) => setProductQ(e.target.value)}
          />
        </div>

        {productResults.length > 0 ? (
          <ul className="flex flex-col gap-1 rounded-sm border border-ink-200 bg-white p-1 max-w-md">
            {productResults.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => void pickProduct(p)}
                  className="flex w-full items-center justify-between gap-3 rounded-sm px-3 py-2 text-left text-body-sm hover:bg-paper-warm"
                >
                  <span className="text-ink-900">{p.title}</span>
                  <span className="text-caption text-ink-500">{p.status}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : productSearching ? (
          <p className="text-body-sm text-ink-500">Searching…</p>
        ) : null}

        {/* Variant picker for the picked product. */}
        {activeProduct ? (
          <div className="flex flex-col gap-2 rounded-sm border border-ink-200 bg-white p-3 max-w-md">
            <div className="flex items-center justify-between">
              <span className="text-body-sm font-medium text-ink-900">
                {activeProduct.title} — choose a variant
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveProduct(null)}
              >
                Cancel
              </Button>
            </div>
            {activeProduct.variants.length === 0 ? (
              <p className="text-body-sm text-ink-500">
                This product has no variants.
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {activeProduct.variants.map((v) => (
                  <li key={v.id}>
                    <button
                      type="button"
                      onClick={() => addLine(activeProduct, v)}
                      className="flex w-full items-center justify-between gap-3 rounded-sm px-3 py-2 text-left text-body-sm hover:bg-paper-warm"
                    >
                      <span className="text-ink-900">{v.title}</span>
                      <span className="text-caption text-ink-500">
                        {v.sku} · {formatGBP(v.base_price_minor)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <OrderLineEditor
          rows={rows}
          priced={pricedByRow}
          onChange={setRows}
        />
      </section>

      {/* ---- 3. Settlement ---- */}
      <section className="flex flex-col gap-3">
        <h2 className="text-body font-medium text-ink-900">Settlement</h2>
        {selectedLocation === null ? (
          <p className="text-body-sm text-ink-500">
            Select a location to choose a settlement method.
          </p>
        ) : settlements.length === 0 ? (
          <p className="text-body-sm text-danger" role="alert">
            COD is not enabled for this company and this location does not allow
            on-account settlement. The order cannot be created here.
          </p>
        ) : (
          <fieldset className="flex flex-col gap-2">
            {settlements.map((s) => (
              <Radio
                key={s}
                name="settlement"
                value={s}
                checked={method === s}
                onChange={() => setMethod(s)}
                label={SETTLEMENT_LABEL[s]}
              />
            ))}
          </fieldset>
        )}
      </section>

      {/* ---- 4. Review + Create ---- */}
      <section className="flex flex-col gap-3">
        <h2 className="text-body font-medium text-ink-900">Review</h2>

        {previewError ? (
          <p className="text-body-sm text-danger" role="alert">
            {previewError}
          </p>
        ) : null}

        {preview ? (
          <dl className="flex flex-col gap-1 self-end min-w-[16rem] text-body-sm">
            <div className="flex justify-between gap-6">
              <dt className="text-ink-500">Subtotal</dt>
              <dd>
                <Numeric>{formatGBP(preview.subtotal_minor)}</Numeric>
              </dd>
            </div>
            <div className="flex justify-between gap-6">
              <dt className="text-ink-500">VAT</dt>
              <dd>
                <Numeric>{formatGBP(preview.vat_minor)}</Numeric>
              </dd>
            </div>
            <div className="flex justify-between gap-6 font-medium text-ink-900">
              <dt>Total</dt>
              <dd>
                <Numeric serif>{formatGBP(preview.total_minor)}</Numeric>
              </dd>
            </div>
          </dl>
        ) : completeRows.length === 0 ? (
          <EmptyState
            icon="package"
            title="No priced lines yet"
            body="Add at least one line and pick a settlement method to see totals."
          />
        ) : previewing ? (
          <p className="text-body-sm text-ink-500">Pricing…</p>
        ) : null}

        <div className="flex items-center gap-3">
          <Button onClick={() => void create()} disabled={!canCreate} loading={creating}>
            Create order
          </Button>
          {!canCreate && !creating ? (
            <span className="text-caption text-ink-500">
              Pick a location, add a line, and choose a settlement method.
            </span>
          ) : null}
        </div>
      </section>
    </div>
  );
}
